import type { ContentHref } from '@generalTypes/apiTypes';
import { ContentType } from '@generalTypes/apiTypes';
import { selectCurrentMinimumGoalRelation } from '@UI/aside/asideMinimumGoal/asideMinimumGoalSelectors';
import { selectPathToRoot } from '@newStore/documentApi/documentApiSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import type { EditComponent } from '@nodeTypeConfig/configTypes';
import { pick } from 'lodash';
import { createError } from '../createError';
import type { AsideValidationRule } from '../validationTypes';

const selectAncestorCluster = createTypedSelector(
  [(state, href: ContentHref) => selectPathToRoot(state, href)],
  (pathToRoot) =>
    pathToRoot.find((ancestor) => ancestor.type === ContentType.KRC_CURRICULUM_CLUSTER) ?? null,
);

export const selectClusterMinimumGoalConsistencyValidation: AsideValidationRule =
  createTypedSelector(
    [
      (state, href, parentHref, config: EditComponent) => {
        return selectCurrentMinimumGoalRelation(state, href, config);
      },
      (state, href) => {
        return selectAncestorCluster(state, href);
      },
      (state, href, parentHref, config: EditComponent) => {
        const clusterHref = selectAncestorCluster(state, href)?.$$meta.permalink;
        if (!clusterHref) {
          return null;
        }
        return selectCurrentMinimumGoalRelation(state, clusterHref, config);
      },
      (state, href, parentHref, config: EditComponent) => config,
    ],
    (currentRelation, ancestorCluster, parentClusterMinimumGoalRel, config) => {
      // If there is no ancestor cluster, there are no consistency requirements
      if (!ancestorCluster) {
        return true;
      }

      // if there is an ancestor cluster, but it doesn't have a minimum goal set, then there are also no consistency requirements
      if (!parentClusterMinimumGoalRel) {
        return true;
      }

      // if there is an ancestor cluster with a minimum goal set, then the minimum goal of the current goal must be the same
      if (currentRelation?.to?.href === parentClusterMinimumGoalRel?.to?.href) {
        return true;
      }

      return createError(
        'clusterMinimumGoalNotAligned',
        'selectClusterMinimumGoalConsistencyValidation',
        'Het minimumdoel van dit doel moet gelijk zijn aan het minimumdoel van de bovenliggende cluster, of die van de cluster moet leeg zijn.',
        pick(config, ['component', 'property']),
      );
    },
  );
